// This component renders the Stripe Subscription pricing table using their API.
import { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Card, PageShell } from 'src/design-system';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from 'src/design-system';
import { openCrispChat, resetCrispSession } from 'src/utils/crisp';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from 'store/hooks';
import type { RootState } from 'src/store';

const logo = '/logo.svg';
import BillingApi from 'src/api/billing';
import { clearTokenInfo } from 'src/utils/authUtils';
import { clearImpersonationStorage } from 'src/store/user/userSlice';
const isStaging = import.meta.env.VITE_API_SERVER_URL?.includes('tucker');

// Define table IDs for trial and paid pricing tables
const TABLE_IDS = {
  trial: {
    light: isStaging ? 'prctbl_1RHurcPx3L8H7CpGnnghGWxc' : 'prctbl_1RwQusLtqyefzHZfgqdPxzdp',
    dark: isStaging ? 'prctbl_1RHurcPx3L8H7CpGnnghGWxc' : 'prctbl_1RwQpRLtqyefzHZfnDqBcZKC'
  },
  paid: {
    light: isStaging ? 'prctbl_paid_light_staging_REPLACE_ME' : 'prctbl_1TEEZ5LtqyefzHZf04vd2dQr',
    dark: isStaging ? 'prctbl_paid_dark_staging_REPLACE_ME' : 'prctbl_1TEEbZLtqyefzHZfgaHaAa5m'
  }
} as const;

// Define subscription tiers
const SUBSCRIPTION_TIERS = {
  tier1: {
    name: 'Scribe',
    description:
      'Perfect for new adventurers managing a single campaign. Capture every word with Discord transcriptions, relive key moments through post-session summaries and highlights, and get quick answers from Scribe.'
  },
  tier2: {
    name: 'Archivist',
    description:
      'For DMs with growing worlds to preserve. Run up to three active campaigns, keep your lore organized with document uploads, and catch up fast with “Campaign So Far” recaps.'
  },
  tier3: {
    name: 'Loremaster',
    description:
      'The full library is yours to command. Enjoy unlimited campaigns, unlock early access to upcoming features, and wield the complete power of The DM’s Ark with all tools and benefits from the lower tiers.'
  }
} as const;
type TierKey = keyof typeof SUBSCRIPTION_TIERS;
const SubscriptionHandler = () => {
  const [secret, setSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth0();

  // changes table id based on preferred browser theme
  const { theme } = useTheme();
  const user = useAppSelector((state: RootState) => state.auth.user);

  // Show trial table only for users that have never had a Stripe subscription status.
  // Returning users (canceled/expired/unpaid/etc.) are shown paid-only table IDs.
  const shouldShowTrialTable = !user?.stripeSubscriptionStatus;

  let tableId: string;

  if (shouldShowTrialTable) {
    if (theme === 'dark') {
      tableId = TABLE_IDS.trial.dark;
    } else {
      tableId = TABLE_IDS.trial.light;
    }
  } else {
    if (theme === 'dark') {
      tableId = TABLE_IDS.paid.dark;
    } else {
      tableId = TABLE_IDS.paid.light;
    }
  }

  // Guard against unfilled staging paid-table placeholders reaching Stripe (they
  // render as a broken table and block returning staging users from subscribing).
  // Fall back to the real staging trial table so staging stays functional; prod
  // IDs are real so this never triggers there. Replace the staging paid IDs with
  // real prctbl_ IDs to remove this fallback.
  if (tableId.includes('REPLACE_ME')) {
    console.warn(
      `Stripe pricing-table id "${tableId}" is a placeholder; falling back to the trial table. Set the real staging paid table IDs.`
    );
    tableId = theme === 'dark' ? TABLE_IDS.trial.dark : TABLE_IDS.trial.light;
  }

  // Check if a tier is specified in URL parameters
  const selectedTier = searchParams.get('tier') as TierKey | null;

  // Function to show Crisp messenger
  const showCrisp = useCallback(() => {
    openCrispChat();
  }, []);
  const handleLogout = () => {
    clearTokenInfo();
    clearImpersonationStorage();
    resetCrispSession();
    logout({
      logoutParams: {
        returnTo: window.location.origin + '/logout'
      }
    });
  };
  const getCustomerSession = async () => {
    const sessionSecret = BillingApi.createStripeCustomerSession();
    const { data } = await sessionSecret;
    setSecret(data.secret);
  };
  const handleTierSelection = async (tier: TierKey) => {
    if (!SUBSCRIPTION_TIERS[tier]) {
      setError('Invalid subscription tier selected');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await BillingApi.createStripeSubscriptionSessionForTier(tier);
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Failed to create subscription session:', err);
      setError('Failed to create subscription session. Please try again.');
      setIsLoading(false);
    }
  };
  useEffect(() => {
    // If a tier is specified, handle it immediately
    if (selectedTier && SUBSCRIPTION_TIERS[selectedTier]) {
      handleTierSelection(selectedTier);
      return;
    }

    // Otherwise, get the customer session for the pricing table
    getCustomerSession();
  }, [selectedTier]);

  // If a tier is selected, show loading state
  if (selectedTier && SUBSCRIPTION_TIERS[selectedTier]) {
    return (
      <PageShell centered className="bg-background">
        <div className="text-center">
          <img src={logo} alt="Logo" className="h-32 w-auto mx-auto mb-8" />
          <h1 className="text-4xl font-medium text-foreground mb-4">
            Setting up your {SUBSCRIPTION_TIERS[selectedTier].name} subscription...
          </h1>
          <p className="text-lg text-foreground mb-4">
            {SUBSCRIPTION_TIERS[selectedTier].description}
          </p>
          {isLoading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2">Redirecting to checkout...</span>
            </div>
          )}
          {error && (
            <div className="mt-4">
              <p className="text-red-500 mb-4">{error}</p>
              <Button onPress={() => navigate('/subscription')} variant="primary">
                Back to Pricing
              </Button>
            </div>
          )}
        </div>
      </PageShell>
    );
  }

  // Show the regular pricing table if no tier is specified
  return secret ? (
    <>
      <script async src="https://js.stripe.com/v3/pricing-table.js"></script>
      <PageShell
        className="flex flex-col items-center bg-background p-4 mt-8"
        contentClassName="max-w-full">
        <div className="w-full flex justify-between items-start mb-4">
          <div className="flex">
            <div>
              <Link to="https://thedmsark.com" className="flex items-center" target="_blank">
                <img src={logo} alt="Logo" className="h-32 w-auto mr-4" />
              </Link>
            </div>
          </div>
          <Button variant="tertiary" onPress={handleLogout} className="mt-2">
            {<FontAwesomeIcon icon={faRightFromBracket} />}
            Logout
          </Button>
        </div>
        <div className="flex">
          <div>
            <h1 className="text-5xl font-medium text-foreground mb-4">
              Choose your <span className="line-through font-medium">plan</span> adventure
            </h1>
            <h3 className="md:text-2xl sm:text-lg font-medium text-foreground mb-4">
              Say goodbye to manual session notes, and say hello to The Ark.
            </h3>
            <p className="italic font-semibold">
              {shouldShowTrialTable ? 'Get your first month free.' : 'Subscribe to continue.'}
            </p>
          </div>
        </div>
        <Card className="w-full p-2 flex flex-col items-center gap-6 mt-4">
          {/* @ts-expect-error Stripe component */}
          <stripe-pricing-table
            pricing-table-id={tableId}
            publishable-key={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}
            className="w-full h-full"
            customer-session-client-secret={secret}
          />
        </Card>

        <div className="mt-2 space-y-2">
          <p className="italic text-center text-sm text-gray-600 dark:text-gray-400">
            Cancel anytime. No questions asked.
          </p>

          <div className="text-center space-y-2">
            <div className="flex flex-wrap justify-center items-center gap-3 text-sm">
              <p className="italic text-sm text-gray-600 dark:text-gray-400">Need help deciding?</p>

              <button
                type="button"
                onClick={showCrisp}
                className="inline-flex items-center gap-2 dark:text-white dark:hover:text-gray-300 transition-colors underline bg-transparent border-none p-0 cursor-pointer">
                <span>Chat with us</span>
              </button>

              <span className="text-gray-500">•</span>

              <Link
                to="https://discord.gg/9NDYNcd7eF"
                target="_blank"
                className="inline-flex items-center gap-2 dark:text-white dark:hover:text-gray-300 transition-colors underline">
                <FontAwesomeIcon icon={faDiscord} />
                <span>Join our Discord</span>
              </Link>

              <span className="text-gray-500">•</span>

              <Link
                to="mailto:help@thedmsark.com"
                className="inline-flex items-center gap-2 dark:text-white dark:hover:text-gray-300 transition-colors underline">
                <span>Email us</span>
              </Link>
            </div>
          </div>
        </div>
      </PageShell>
    </>
  ) : null;
};
export default SubscriptionHandler;
